import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  TextField,
  useTheme,
  useMediaQuery,
  Stack,
  InputAdornment,
  Tooltip,
  Grid,
  CircularProgress,
  IconButton,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import ordenCompraService, { OrdenCompra, OrdenCompraSearchParams } from '../../services/ordenCompraService';
import { Proveedor } from '../../services/proveedorService';
import ProveedorSearch from '../../components/ProveedorSearch';
import { formatCurrency, formatDate } from '../../utils/formatters';
import PDFViewer from '../../components/shared/PDFViewer';

const getFirstDayOfMonth = (): string => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().split('T')[0];
};

const getLastDayOfMonth = (): string => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  return d.toISOString().split('T')[0];
};

const formatearFecha = (fecha: Date | string | null | undefined): string => {
  if (!fecha) return '-';
  const d = typeof fecha === 'string' ? new Date(fecha) : fecha;
  if (isNaN(d.getTime())) return '-';
  return formatDate(d.toISOString().split('T')[0]);
};

const formatoComprobante = (letra?: string, nroSuc?: number, nroComprobante?: number): string => {
  const L = (letra ?? '').trim() || '-';
  const suc = String(nroSuc ?? 0).padStart(4, '0');
  const nro = String(nroComprobante ?? 0).padStart(8, '0');
  return `${L}-${suc}-${nro}`;
};

const OrdenesCompra: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [ordenes, setOrdenes] = useState<OrdenCompra[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [printingId, setPrintingId] = useState<number | null>(null);
  const [openProveedorSearch, setOpenProveedorSearch] = useState(false);
  const [nombreProveedorFiltro, setNombreProveedorFiltro] = useState('');
  const [pdfPreview, setPdfPreview] = useState<{ url: string; nro: number; title: string } | null>(null);
  const pdfPreviewRef = useRef(pdfPreview);
  pdfPreviewRef.current = pdfPreview;
  const [filters, setFilters] = useState(() => {
    const desde = searchParams.get('fechaDesde');
    const hasta = searchParams.get('fechaHasta');
    return {
      searchTerm: '',
      fechaDesde: desde && /^\d{4}-\d{2}-\d{2}$/.test(desde) ? desde : getFirstDayOfMonth(),
      fechaHasta: hasta && /^\d{4}-\d{2}-\d{2}$/.test(hasta) ? hasta : getLastDayOfMonth(),
      nroProveedor: '',
      letra: '',
      nroSuc: '',
      nroComprobante: '',
      usuario: '',
    };
  });

  const fetchOrdenes = useCallback(
    async (pageNum: number = page, pageSize: number = rowsPerPage, filtros: typeof filters = filters) => {
      try {
        setLoading(true);
        const params: OrdenCompraSearchParams = {
          pageNumber: pageNum + 1,
          pageSize,
          searchTerm: filtros.searchTerm || undefined,
          fechaDesde: filtros.fechaDesde ? new Date(filtros.fechaDesde) : undefined,
          fechaHasta: filtros.fechaHasta ? new Date(filtros.fechaHasta) : undefined,
          nroProveedor: filtros.nroProveedor || undefined,
          letra: filtros.letra || undefined,
          nroSuc: filtros.nroSuc ? parseInt(filtros.nroSuc, 10) : undefined,
          nroComprobante: filtros.nroComprobante ? parseInt(filtros.nroComprobante, 10) : undefined,
          usuario: filtros.usuario || undefined,
        };
        const result = await ordenCompraService.getOrdenesCompra(params);
        setOrdenes(result.items || []);
        setTotalCount(result.totalCount || 0);
      } catch (error: unknown) {
        console.error('Error al cargar órdenes de compra:', error);
        const msg =
          error && typeof error === 'object' && 'response' in error
            ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
            : 'Error al cargar órdenes de compra';
        enqueueSnackbar(msg || 'Error al cargar órdenes de compra', { variant: 'error' });
        setOrdenes([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar]
  );

  useEffect(() => {
    fetchOrdenes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const desde = searchParams.get('fechaDesde');
    const hasta = searchParams.get('fechaHasta');
    setFilters((f) => ({
      ...f,
      ...(desde && /^\d{4}-\d{2}-\d{2}$/.test(desde) ? { fechaDesde: desde } : {}),
      ...(hasta && /^\d{4}-\d{2}-\d{2}$/.test(hasta) ? { fechaHasta: hasta } : {}),
    }));
    setPage(0);
  }, [searchParams]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPage(0);
      fetchOrdenes(0, rowsPerPage, filters);
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [filters]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    fetchOrdenes(newPage, rowsPerPage, filters);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchOrdenes(0, newRowsPerPage, filters);
  };

  const handleBuscar = () => {
    setPage(0);
    fetchOrdenes(0, rowsPerPage, filters);
  };

  const handleProveedorSelect = (proveedor: Proveedor) => {
    setFilters((f) => ({ ...f, nroProveedor: String(proveedor.nroProveedor) }));
    setNombreProveedorFiltro((proveedor.nombre ?? '').trim());
  };

  const handleClosePdfPreview = () => {
    setPdfPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev.url);
      return null;
    });
  };

  const handleImprimirPdf = async (orden: OrdenCompra) => {
    const nroFormulario = orden.nroFormulario;
    try {
      setPrintingId(nroFormulario);
      const blob = await ordenCompraService.getOrdenCompraPdf(nroFormulario);
      const url = URL.createObjectURL(blob);
      setPdfPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev.url);
        return {
          url,
          nro: nroFormulario,
          title: `OC ${formatoComprobante(orden.letra, orden.nroSuc, orden.nroComprobante ?? undefined)}`,
        };
      });
    } catch (e) {
      console.error(e);
      enqueueSnackbar('No se pudo generar el PDF de la orden de compra', { variant: 'error' });
    } finally {
      setPrintingId(null);
    }
  };

  useEffect(() => {
    return () => {
      const p = pdfPreviewRef.current;
      if (p?.url) URL.revokeObjectURL(p.url);
    };
  }, []);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Órdenes de Compra
            </Typography>
            <Button
              variant="contained"
              sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}
              startIcon={<AddIcon />}
              onClick={() => navigate('/compras/ordenes/nuevo')}
            >
              Nueva OC
            </Button>
          </Stack>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar..."
                value={filters.searchTerm}
                onChange={(e) => setFilters((f) => ({ ...f, searchTerm: e.target.value }))}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#1976d2' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Fecha desde"
                value={filters.fechaDesde}
                onChange={(e) => setFilters((f) => ({ ...f, fechaDesde: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Fecha hasta"
                value={filters.fechaHasta}
                onChange={(e) => setFilters((f) => ({ ...f, fechaHasta: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <TextField
                fullWidth
                size="small"
                placeholder="Letra"
                value={filters.letra}
                onChange={(e) => setFilters((f) => ({ ...f, letra: e.target.value.toUpperCase() }))}
                inputProps={{ maxLength: 1 }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder="Suc"
                value={filters.nroSuc}
                onChange={(e) => setFilters((f) => ({ ...f, nroSuc: e.target.value }))}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder="Nro. Comp."
                value={filters.nroComprobante}
                onChange={(e) => setFilters((f) => ({ ...f, nroComprobante: e.target.value }))}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Nro. proveedor"
                placeholder="Ej: 1001"
                value={filters.nroProveedor}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '');
                  setFilters((f) => ({ ...f, nroProveedor: v }));
                  setNombreProveedorFiltro('');
                }}
                inputProps={{ inputMode: 'numeric', 'aria-label': 'Número de proveedor' }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Buscar proveedor por nombre">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => setOpenProveedorSearch(true)}
                          aria-label="Buscar proveedor por nombre"
                          sx={{ color: '#1976d2' }}
                        >
                          <SearchIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                helperText={nombreProveedorFiltro ? `Nombre: ${nombreProveedorFiltro}` : undefined}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                placeholder="Usuario"
                value={filters.usuario}
                onChange={(e) => setFilters((f) => ({ ...f, usuario: e.target.value }))}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                variant="outlined"
                onClick={handleBuscar}
                sx={{ borderRadius: 2, color: '#1976d2', borderColor: '#1976d2' }}
              >
                Buscar
              </Button>
            </Grid>
          </Grid>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <CircularProgress sx={{ color: '#1976d2' }} />
            </Box>
          ) : isMobile ? (
            <>
              <Stack spacing={2}>
                {ordenes.map((o) => (
                  <Card key={o.nroFormulario} variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {formatoComprobante(o.letra, o.nroSuc, o.nroComprobante ?? undefined)} - {o.nombre || 'Sin nombre'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Nro. Formulario: {o.nroFormulario} | Fecha: {formatearFecha(o.fechaEmision)} | Total:{' '}
                        {formatCurrency(o.importeTotal ?? 0)}
                      </Typography>
                      <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
                        <Tooltip title="Ver">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/compras/ordenes/${o.nroFormulario}`)}
                            sx={{ color: '#1976d2' }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Vista previa PDF">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleImprimirPdf(o)}
                              disabled={printingId === o.nroFormulario}
                              sx={{
                                color: '#fff',
                                bgcolor: '#1976d2',
                                borderRadius: 1.5,
                                '&:hover': { bgcolor: '#1565c0' },
                                '&.Mui-disabled': {
                                  bgcolor: 'action.disabledBackground',
                                  color: 'action.disabled',
                                },
                              }}
                            >
                              {printingId === o.nroFormulario ? (
                                <CircularProgress size={18} sx={{ color: '#fff' }} />
                              ) : (
                                <PrintIcon sx={{ fontSize: 20 }} />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </Card>
                ))}
              </Stack>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 20]}
                labelRowsPerPage="Filas:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : to}`}
                sx={{ mt: 2 }}
              />
            </>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Nro. Formulario</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Comprobante</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Proveedor</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Total
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ordenes.map((o) => (
                    <TableRow key={o.nroFormulario} hover>
                      <TableCell>{o.nroFormulario}</TableCell>
                      <TableCell>{formatearFecha(o.fechaEmision)}</TableCell>
                      <TableCell>{formatoComprobante(o.letra, o.nroSuc, o.nroComprobante ?? undefined)}</TableCell>
                      <TableCell>
                        {o.nroProveedor != null ? `${o.nroProveedor} - ` : ''}
                        {o.nombre || '-'}
                      </TableCell>
                      <TableCell align="right">{formatCurrency(o.importeTotal ?? 0)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Ver">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/compras/ordenes/${o.nroFormulario}`)}
                            sx={{ color: '#1976d2' }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Vista previa PDF">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleImprimirPdf(o)}
                              disabled={printingId === o.nroFormulario}
                              sx={{
                                color: '#fff',
                                bgcolor: '#1976d2',
                                borderRadius: 1.5,
                                '&:hover': { bgcolor: '#1565c0' },
                                '&.Mui-disabled': {
                                  bgcolor: 'action.disabledBackground',
                                  color: 'action.disabled',
                                },
                              }}
                            >
                              {printingId === o.nroFormulario ? (
                                <CircularProgress size={18} sx={{ color: '#fff' }} />
                              ) : (
                                <PrintIcon sx={{ fontSize: 20 }} />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 20]}
                labelRowsPerPage="Filas:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : to}`}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <ProveedorSearch
        open={openProveedorSearch}
        onClose={() => setOpenProveedorSearch(false)}
        onSelect={handleProveedorSelect}
      />

      {pdfPreview && (
        <PDFViewer
          pdfUrl={pdfPreview.url}
          onClose={handleClosePdfPreview}
          title={pdfPreview.title}
          documentId={`${pdfPreview.nro}`}
          documentType="ordenCompra"
          downloadFileName={`orden_compra_${pdfPreview.nro}.pdf`}
        />
      )}
    </Box>
  );
};

export default OrdenesCompra;
